import { useState } from 'react';
import { Drawer, Head, linkedDrawers, SaveView, sortOf, useAdmin, useDrawerRoute, useFilters } from '../ui/Shell';
import { Block, Cell, DateRange, Grid, ListMore, Pill, Table, type Row, type Tone } from '../ui/Block';
import { fmtDate, post, type Env } from '../lib/api';
import { describeErr, hhmmss } from '../lib/hooks';
import { countOf, qs, usePagedList, type Paged } from '../lib/list';

type Status = 'info' | 'warning' | 'error';
interface Notification {
  id: string; kind: string; status: Status; title: string; description: string; principalId: string | null; principalName: string | null;
  data: Record<string, unknown>; createdAt: string; readAt: string | null; readBy: string | null;
  resolvedAt: string | null;
}
const TONE: Record<Status, Tone> = { info: 'info', warning: 'warn', error: 'bad' };

function NotificationDrawer({ n, env, onRead }: { n: Notification; env: Env; onRead: () => void }) {
  const ctx = useAdmin();
  const ticket = typeof n.data.ticketId === 'string' ? n.data.ticketId : null;
  const flag = n.kind === 'moderation' && typeof n.data.flagId === 'string' ? n.data.flagId : null;
  const csam = n.data.category === 'csam';
  const resolve = async (how: 'restore' | 'confirm') => {
    if (!flag) return;
    if (how === 'restore' && !window.confirm('Restore this transcription? The owner gets it back and its share links work again.')) return;
    try {
      await post(env, `/admin/moderation/${flag}/${how}`);
      if (!n.readAt) await post(env, `/admin/notifications/${n.id}/read`);
      ctx.toast(how === 'restore' ? 'Transcription restored' : 'Block confirmed', n.title);
      onRead(); ctx.reloadAttention(); ctx.closeDrawer();
    } catch (e) {
      ctx.toast(how === 'restore' ? 'Not restored' : 'Not confirmed', e instanceof Error ? e.message : String(e), { logged: false });
    }
  };
  const report = async () => {
    if (!flag) return;
    if (!window.confirm('Report this to NCMEC\'s CyberTipline?\n\nOnly for apparent child sexual abuse material: you have looked at the evidence and it is that. The report sends the evidence, the account (email, name, id) and its IP addresses and devices. It also confirms the block. This cannot be undone.')) return;
    try {
      const r = await post<{ reportId: string }>(env, `/admin/moderation/${flag}/report-ncmec`);
      if (!n.readAt) await post(env, `/admin/notifications/${n.id}/read`);
      ctx.toast('Reported to NCMEC', `Report ${r.reportId}`);
      onRead(); ctx.reloadAttention(); ctx.closeDrawer();
    } catch (e) {
      ctx.toast('Not reported', e instanceof Error ? e.message : String(e), { logged: false });
    }
  };
  return (
    <Drawer eyebrow={`NOTIFICATION · ${n.kind}`} title={n.title} sub={fmtDate(n.createdAt)} chip={n.resolvedAt ? <Pill tone="ok">RESOLVED</Pill> : <Pill tone={TONE[n.status]}>{n.status.toUpperCase()}</Pill>}
      acts={<>
        {ticket && <button type="button" className="btn btn--sm" onClick={() => { ctx.closeDrawer(); ctx.go('support', { id: ticket }); }}>Open ticket</button>}
        {flag && !csam && <button type="button" className="btn btn--sm" onClick={() => resolve('restore')}>Restore</button>}
        {flag && <button type="button" className="btn btn--sm" onClick={() => resolve('confirm')}>Confirm block</button>}
        {flag && <button type="button" className="btn btn--sm" onClick={report}>Report to NCMEC</button>}
        {!n.readAt && !n.resolvedAt && <button type="button" className="btn btn--sm" onClick={async () => { await post(env, `/admin/notifications/${n.id}/read`); onRead(); ctx.reloadAttention(); ctx.closeDrawer(); }}>Mark read</button>}
      </>} wide>
      <div className="adm__html" style={{ fontSize: 13, lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: n.description }} />
      {n.principalId && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-62)' }}>Account: {n.principalName ?? '—'} <span className="mono">{n.principalId}</span></div>}
      {n.resolvedAt && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-62)' }}>Resolved {fmtDate(n.resolvedAt)}: the cause went away, no repeat.</div>}
      {n.readAt && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-45)' }}>Read {fmtDate(n.readAt)}{n.readBy ? ` by ${n.readBy}` : ''}</div>}
      <pre style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--fill-05)', fontSize: 11.5, overflowX: 'auto' }}>{JSON.stringify(n.data, null, 2)}</pre>
    </Drawer>
  );
}

function EveryoneForm({ env, onDone }: { env: Env; onDone: () => void }) {
  const ctx = useAdmin();
  const [status, setStatus] = useState<Status>('info');
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await post(env, '/admin/notifications/everyone', { status, title: title.trim(), html: html.trim(), link: link.trim() || null });
      ctx.toast('Posted to every account', title.trim());
      ctx.closeModal();
      onDone();
    } catch (e) {
      ctx.toast('Not posted', e instanceof Error ? e.message : String(e), { logged: false });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal modal--wide" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="modal__title">Notify every account</div>
      <div className="modal__body">Shows in every customer's Dashboard notifications (no email). For releases, maintenance windows, new voices. HTML is cut to p, br, strong, em, code, lists and links to app paths.</div>
      <select className="sel" value={status} onChange={(e) => setStatus(e.target.value as Status)} aria-label="Status">
        <option value="info">Info</option><option value="warning">Warning</option><option value="error">Error</option>
      </select>
      <input className="field__input" placeholder="Short title, e.g. Scheduled maintenance" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="field__input mono" rows={5} placeholder={'<p>Live transcription may reconnect once on <strong>Oct 14, 02:00–02:20 UTC</strong>.</p>'} value={html} onChange={(e) => setHtml(e.target.value)} style={{ resize: 'vertical', fontSize: 12.5 }} />
      <input className="field__input mono" placeholder="Link (an app path, optional): /voices" value={link} onChange={(e) => setLink(e.target.value)} />
      {html.trim() && <div style={{ padding: 10, borderRadius: 8, background: 'var(--fill-05)', fontSize: 13 }} dangerouslySetInnerHTML={{ __html: html }} />}
      <div className="modal__acts">
        <button type="button" className="btn btn--sm" onClick={ctx.closeModal}>Cancel</button>
        <button type="button" className="btn btn--sm btn--primary" disabled={busy || !title.trim()} onClick={send}>{busy ? 'Posting…' : 'Post to everyone'}</button>
      </div>
    </div>
  );
}

type Filter = 'unread' | 'all' | Status;

export function Notifications() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [f, set] = useFilters({ filter: linkedDrawers().some((k) => k.startsWith('notification:')) ? 'all' : 'unread', kind: '', from: '', to: '', sort: '', dir: '' });
  const s = sortOf(f, set, { key: 'created', dir: 'desc' });
  const filter = f.filter as Filter;
  const path = `/admin/notifications${qs({ unread: filter === 'unread', status: filter !== 'unread' && filter !== 'all' ? filter : '', kind: f.kind, from: f.from, to: f.to, sort: f.sort, dir: f.sort ? s.sort.dir : '' })}`;
  const l = usePagedList<Notification, Paged & { notifications: Notification[]; unread: number }>(env, path, 'notifications', 30, paused);
  const unread = l.data?.unread ?? 0;

  const readAll = async () => {
    const r = await post<{ updated: number }>(env, '/admin/notifications/read');
    ctx.toast(`Marked ${r.updated} read`);
    l.reload();
    ctx.reloadAttention();
  };

  const drawer = (n: Notification) => <NotificationDrawer n={n} env={env} onRead={l.reload} />;
  useDrawerRoute('notification', (id) => { if (!l.data) return undefined; const n = l.rows.find((x) => x.id === id); return n ? drawer(n) : null; }, [l.data, l.rows]);

  const rows: Row[] = l.rows.map((n) => ({
    key: n.id,
    onClick: () => ctx.openDrawer(`notification:${n.id}`, drawer(n)),
    cells: [
      { pill: n.resolvedAt ? { label: 'RESOLVED', tone: 'ok' } : { label: n.status.toUpperCase(), tone: TONE[n.status] } },
      { t: n.title, fw: n.readAt || n.resolvedAt ? undefined : 600, sub: n.principalName ?? undefined },
      { t: n.kind, mono: true },
      { t: fmtDate(n.createdAt), mono: true, sub: n.resolvedAt ? 'resolved' : n.readAt ? 'read' : undefined },
    ],
  }));
  const chip = (v: Filter, label: string) => ({ label, on: filter === v, onClick: () => set({ filter: v }) });

  return (
    <>
      <Head title="Notifications" sub="What needs a look: emails given up on, failed transcriptions, suspended accounts, support messages, new subscriptions. Unread errors also show in the Inbox."
        acts={<button type="button" className="btn btn--sm" onClick={() => ctx.openModal(<EveryoneForm env={env} onDone={l.reload} />)}>Notify every account</button>} />
      <Grid>
        <Cell span={12}>
          <Block title="Notifications" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} loading={l.loading && !l.data}
            error={describeErr(l.error) ? { ...describeErr(l.error)!, retry: l.reload } : null}
            chips={[chip('unread', `Unread · ${unread}`), chip('error', 'Errors'), chip('warning', 'Warnings'), chip('info', 'Info'), chip('all', 'All')]}
            search={{ value: f.kind, placeholder: 'Kind, e.g. stream_failed', onChange: (v) => set({ kind: v.trim() }) }}
            extra={<><DateRange label="Raised" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} /><SaveView /></>}
            headActs={unread > 0 ? [{ label: 'Mark all read', onClick: readAll }] : undefined}
            empty={l.data && l.rows.length === 0 ? (filter === 'unread' ? 'All caught up.' : 'Nothing here.') : undefined}
            emptyGood={filter === 'unread'}
            foot={<ListMore list={l} noun="notifications" />}>
            <Table mobile={mobile} minWidth={760} rows={rows} sort={s.sort} onSort={s.onSort}
              cols={[{ label: 'STATUS', width: '110px', sort: 'status', sortAsc: true }, { label: 'TITLE', width: 'minmax(280px,2.5fr)', sort: 'title', sortAsc: true }, { label: 'KIND', width: 'minmax(160px,1fr)', sort: 'kind', sortAsc: true }, { label: 'WHEN', width: '140px', sort: 'created' }]} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
